<template>
  <div ref="rootEl" class="q-table__control row no-wrap full-width justify-end items-center">
    <TableBottomClearButton
      v-if="totalUnfiltered !== undefined"
      :class="{ 'q-mr-lg': showPageControls }"
      :total="total"
      :total-unfiltered="totalUnfiltered"
      @click="$emit('clear-filters')"
    />
    <div v-if="showPageControls" class="column justify-end items-center">
      <div v-if="showAllRecords && moreResults" class="col q-mt-sm">
        <LoadMoreButton :label="loadMoreLabel" @click="$emit('request')" />
      </div>
      <div class="row items-center">
        <span class="q-table__bottom-item">Records per page:</span>
        <q-select
          v-capacitor-native-select
          borderless
          class="q-table__bottom-item"
          :class="{ 'q-mr-none': !hasPages }"
          dense
          :display-value="showAllRecords ? 'All' : rowsPerPage"
          emit-value
          hide-bottom-space
          map-options
          :model-value="rowsPerPage"
          :options="rowsPerPageOptions"
          @update:model-value="update('rowsPerPage', $event)"
        />
        <div v-if="hasPages" class="q-table__control">
          <span class="q-table__bottom-item"
            >{{ firstItemIndex }}-{{ lastItemIndex }} of {{ total }}{{ moreResults ? '+' : '' }}</span
          >
          <q-btn dense :disable="page === 1" flat icon="chevron_left" round @click="update('page', page - 1)" />
          <q-btn dense :disable="isLastPage" flat icon="chevron_right" round @click="update('page', page + 1)" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { QTable } from 'quasar';
import { computed, nextTick, ref, watch } from 'vue';
import LoadMoreButton from 'components/LoadMoreButton.vue';
import TableBottomClearButton from 'components/table/TableBottomClearButton.vue';
import { ROWS_PER_PAGE_OPTIONS } from 'src/services/constants';

type UpdatableProp = 'rowsPerPage' | 'page';
type PaginationDetails = NonNullable<QTable['pagination']>;

type BaseProps = {
  loadMoreLabel?: string;
  moreResults?: boolean;
  total: number;
  totalUnfiltered?: number;
};

type PaginationProps = BaseProps & {
  pagination: PaginationDetails;
  page?: never;
  rowsPerPage?: never;
};

type PageRowsProps = BaseProps & {
  pagination?: undefined;
  page: number;
  rowsPerPage: number;
};

type Props = PaginationProps | PageRowsProps;

const props = defineProps<Props>();

const emit = defineEmits(['clear-filters', 'request', 'update:page', 'update:rowsPerPage', 'update:pagination']);

const rootEl = ref<HTMLElement | null>(null);

const ensureProvided = (value: number | undefined, propName: UpdatableProp) => {
  if (value === undefined) {
    throw new Error(
      `TableBottom requires either the pagination prop or both page and rowsPerPage props (missing ${propName}).`
    );
  }

  return value;
};

const page = computed<number>(() => ensureProvided(props.pagination ? props.pagination.page : props.page, 'page'));
const rowsPerPage = computed<number>(() =>
  ensureProvided(props.pagination ? props.pagination.rowsPerPage : props.rowsPerPage, 'rowsPerPage')
);

const options = ref<number[]>([]);

const syncOptions = (value: number) => {
  const uniqueValues = new Set<number>(ROWS_PER_PAGE_OPTIONS);
  uniqueValues.add(value);

  const sorted = Array.from(uniqueValues).sort((current, next) => (current > next ? 1 : -1));
  const zeroIndex = sorted.indexOf(0);

  if (zeroIndex !== -1) {
    const [allValue] = sorted.splice(zeroIndex, 1);
    sorted.push(allValue); // Make sure "All" is last option
  }

  options.value = sorted;
};

watch(rowsPerPage, (value) => syncOptions(value), { immediate: true });

const firstItemIndex = computed(() => (page.value - 1) * rowsPerPage.value + 1);
const lastItemIndex = computed(() => {
  const lastIndex = firstItemIndex.value + rowsPerPage.value - 1;
  return lastIndex > props.total ? props.total : lastIndex;
});

/**
 * Has pages if:
 * - the last item is less than the total or we're on a page other than the 1st
 * - and: we're not displaying all records
 * - and: the total is greater than the lowest rows-per-page option
 */
const hasPages = computed(() => (lastItemIndex.value < props.total || page.value > 1) && rowsPerPage.value > 0);

const isLastPage = computed(() => firstItemIndex.value + rowsPerPage.value > props.total);

const rowsPerPageOptions = computed(() =>
  options.value.map((value) => ({
    label: value === 0 ? 'All' : value,
    value,
  }))
);

const showAllRecords = computed(() => rowsPerPage.value === 0);
const showPageControls = computed(() => props.total > (options.value[0] ?? 0));

const update = async (prop: UpdatableProp, value: number) => {
  if (!rootEl.value) {
    return;
  }

  const element = rootEl.value;
  const { top: previousTop } = element.getBoundingClientRect();

  if (props.pagination) {
    emit('update:pagination', {
      ...props.pagination,
      [prop]: value,
    });
  }

  emit(`update:${prop}`, value);

  await nextTick();

  let currentParent: HTMLElement | null = element;
  let scrolledElement: HTMLElement | undefined;

  for (let i = 0; i < 10; i += 1) {
    currentParent = (currentParent?.parentNode as HTMLElement) ?? null;

    if (currentParent instanceof HTMLElement) {
      const { classList } = currentParent;

      if (classList.contains('q-panel') || classList.contains('dialog--body')) {
        scrolledElement = currentParent;
        break;
      }
    }
  }

  const scrollableParent: HTMLElement | Window = scrolledElement ?? window;
  const { top } = element.getBoundingClientRect();
  const parentScrollTop = scrollableParent instanceof Window ? scrollableParent.scrollY : scrollableParent.scrollTop;

  scrollableParent.scrollTo(0, parentScrollTop + (top - previousTop));
};

watch(isLastPage, (value) => {
  if (value && props.moreResults) {
    emit('request');
  }
});
</script>
